import httpClient from "../http/http-client";
import { IOrganisationDetails } from '../organisation/organisation.model';
import { IPartnerClientReadiness, IPartnerClientsOverview, IPostPartnerOrganisation } from './partner.model';
import { ICreateTaskFromFindingPayload } from '../../../components/presentation-components/readiness-components/create-task-from-finding-dialog';

export default class PartnerService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getAllClients(): Promise<IOrganisationDetails[]> {
    return this.http.get(`partner/clients`)
      .then(response => {
        // Unpaginated, the endpoint returns a plain array; only the paginated form
        // wraps rows. Reading .rows off the array returned undefined and crashed the
        // partner banner's client book (white screen on entering a client).
        return Array.isArray(response.data) ? response.data : response.data?.rows ?? []
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getAllClientsWithPagination(searchQuery: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: IOrganisationDetails[] }> {
    return this.http.get(`partner/clients?nameIncludes=${searchQuery}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getClientsOverview(): Promise<IPartnerClientsOverview> {
    return this.http.get(`partner/clients/overview`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  // The client's readiness evaluation, open tasks and assignable users for one period (the
  // client's active period when none is given). Every call is written to the client's audit
  // trail server-side, so callers fetch once per view rather than per component.
  async getClientReadiness(clientId: string, reportPeriodId?: string | null): Promise<IPartnerClientReadiness> {
    return this.http.get(`partner/client/${clientId}/readiness${reportPeriodId ? `?reportPeriodId=${reportPeriodId}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async createClientTaskFromFinding(clientId: string, reportPeriodId: string, payload: ICreateTaskFromFindingPayload): Promise<{ id: string }> {
    return this.http.post(`partner/client/${clientId}/readiness/${reportPeriodId}/create-task`, payload)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getOne(clientId: string): Promise<any> {
    return this.http.get(`partner/client/${clientId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async connectToClient(clientId: string): Promise<any> {
    return this.http.post(`partner/client/connect`, { organisationId: clientId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async disconnectFromClient(): Promise<any> {
    return this.http.post(`partner/client/disconnect`, {})
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async createClient(client: any): Promise<any> {
    return this.http.post(`partner/create-organisation`, client)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async editPartner(updatedOrganisationDetails: IPostPartnerOrganisation): Promise<IOrganisationDetails> {
    return this.http.patch('partner/update', updatedOrganisationDetails)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async savePartnerLogo(details: any): Promise<IOrganisationDetails> {
    return this.http.post('partner/save-logo', details)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }


  async savePartnerIcon(details: any): Promise<IOrganisationDetails> {
    return this.http.post('partner/save-icon', details)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async saveReversedLogo(details: any): Promise<IOrganisationDetails> {
    return this.http.post('partner/save-reversed-logo', details)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err
      });
  }
}